import React from 'react';
import { useTheme } from '../layout/ThemeProvider';

export interface LoadingSpinnerProps {
  variant?: 'rocket' | 'satellite' | 'orbit' | 'stars' | 'simple' | 'astronaut';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'secondary' | 'orange' | 'purple';
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  variant = 'rocket',
  size = 'md',
  color = 'primary',
  className = '',
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark' || theme === 'mixed';

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };

  const getColorStyles = () => {
    switch (color) {
      case 'orange':
        return { color: '#F76700' };
      case 'purple':
        return { color: '#7B5D99' };
      case 'secondary':
        return { color: 'var(--space-text-secondary)' };
      default:
        return { color: 'var(--space-text-primary)' };
    }
  };

  const renderSpinner = () => {
    switch (variant) {
      case 'rocket':
        return (
          <div className={`${sizeClasses[size]} ${className}`}>
            <svg 
              className="animate-rocket-float" 
              fill="currentColor" 
              viewBox="0 0 24 24"
              style={getColorStyles()}
            >
              <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" />
              <path d="M12 16L13.5 18.5L15 21H9L10.5 18.5L12 16Z" opacity="0.6" />
            </svg>
          </div>
        );

      case 'satellite':
        return (
          <div className={`${sizeClasses[size]} ${className} relative`}>
            <div className="animate-satellite-orbit">
              <svg
                className="w-full h-full"
                fill="currentColor"
                viewBox="0 0 24 24"
                style={getColorStyles()}
              >
                <rect x="10" y="10" width="4" height="4" rx="1" />
                <rect x="8" y="11" width="2" height="2" opacity="0.6" />
                <rect x="14" y="11" width="2" height="2" opacity="0.6" />
                <rect x="11" y="8" width="2" height="2" opacity="0.6" />
                <rect x="11" y="14" width="2" height="2" opacity="0.6" />
              </svg>
            </div>
          </div>
        );

      case 'orbit':
        return (
          <div className={`${sizeClasses[size]} ${className} relative`}>
            <div className="absolute inset-0 border-2 border-current rounded-full opacity-20" />
            <div className="absolute inset-1 border-2 border-current rounded-full opacity-40" />
            <div 
              className="absolute w-2 h-2 bg-current rounded-full animate-satellite-orbit"
              style={{ 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)',
                ...getColorStyles()
              }}
            />
          </div>
        );

      case 'stars':
        return (
          <div className={`${sizeClasses[size]} ${className} relative`}>
            <div className="absolute inset-0 flex items-center justify-center">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-current rounded-full animate-twinkle"
                  style={{
                    transform: `rotate(${i * 72}deg) translateY(-${size === 'sm' ? '8' : size === 'md' ? '12' : size === 'lg' ? '18' : '24'}px)`,
                    animationDelay: `${i * 0.2}s`,
                    ...getColorStyles()
                  }}
                />
              ))}
            </div>
          </div>
        );

      case 'astronaut':
        return (
          <div className={`${sizeClasses[size]} ${className} relative`}>
            <img 
              src="/study-bel-v2.png" 
              alt="StudyBel Astronaut"
              className="w-full h-full object-contain animate-rocket-float filter drop-shadow-lg"
              style={{
                filter: isDark 
                  ? 'drop-shadow(0 0 20px rgba(247, 103, 0, 0.4)) brightness(1.1)' 
                  : 'drop-shadow(0 0 15px rgba(26, 15, 43, 0.3))'
              }}
            />
            {/* Subtle glow ring around astronaut */}
            <div 
              className="absolute inset-0 rounded-full animate-pulse"
              style={{
                background: `radial-gradient(circle, transparent 60%, ${isDark ? 'rgba(247, 103, 0, 0.2)' : 'rgba(123, 93, 153, 0.2)'} 100%)`,
                pointerEvents: 'none'
              }}
            />
          </div>
        );

      case 'simple':
      default:
        return (
          <div 
            className={`${sizeClasses[size]} ${className} animate-spin border-2 border-current border-t-transparent rounded-full`}
            style={getColorStyles()}
          />
        );
    }
  };

  return renderSpinner();
};

// Full-screen loading component
export interface FullScreenLoadingProps {
  message?: string;
  variant?: LoadingSpinnerProps['variant'];
  emoji?: string;
}

export const FullScreenLoading: React.FC<FullScreenLoadingProps> = ({
  message = 'Loading...',
  variant = 'astronaut',
  emoji = '🚀',
}) => {
  const { theme } = useTheme();
  const useDarkBackground = theme === 'dark' || theme === 'mixed';

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ 
        background: useDarkBackground 
          ? 'linear-gradient(135deg, #1A1625 0%, #2A203C 100%)'
          : 'linear-gradient(135deg, #FAFAFA 0%, #F0F4F8 100%)'
      }}
    >
      {/* Background Stars */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 50 }, (_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-vibrant-orange rounded-full animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Loading Content */}
      <div className="relative text-center">
        {/* Brand Astronaut */}
        <div className="mb-8 flex justify-center">
          {variant === 'astronaut' ? (
            <div className="relative">
                <LoadingSpinner variant="astronaut" size="xl" />
              {/* Brand name below astronaut */}
              <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                <h1 className="text-2xl font-bold text-gradient">StudyBel</h1>
                <p 
                  className="text-sm opacity-80 mt-1"
                  style={{ color: 'var(--space-text-secondary)' }}
                >
                  AI University Study Assistant
                </p>
              </div>
            </div>
          ) : (
            <LoadingSpinner variant={variant} size="xl" color="orange" />
          )}
        </div>
        
        <div className={`space-y-4 ${variant === 'astronaut' ? 'mt-16' : ''}`}>
          {variant !== 'astronaut' && <div className="text-4xl">{emoji}</div>}
          <h2 
            className={`${variant === 'astronaut' ? 'text-lg' : 'text-xl'} font-semibold`}
            style={{ color: 'var(--space-text-primary)' }}
          >
            {message}
          </h2>
          <div className="w-48 h-1 bg-gray-200 rounded-full mx-auto overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-vibrant-orange to-muted-purple rounded-full animate-pulse"
              style={{ width: '60%' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Inline loading component for buttons and cards
export interface InlineLoadingProps {
  text?: string;
  variant?: LoadingSpinnerProps['variant'];
  size?: LoadingSpinnerProps['size'];
}

export const InlineLoading: React.FC<InlineLoadingProps> = ({
  text,
  variant = 'simple',
  size = 'sm',
}) => {
  return (
    <div className="flex items-center space-x-2">
      <LoadingSpinner variant={variant} size={size} />
      {text && (
        <span 
          className="text-sm font-medium"
          style={{ color: 'var(--space-text-secondary)' }}
        >
          {text}
        </span>
      )}
    </div>
  );
};

// Loading overlay for components
export interface LoadingOverlayProps {
  loading: boolean;
  children: React.ReactNode;
  variant?: LoadingSpinnerProps['variant'];
  message?: string;
  className?: string;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  loading,
  children,
  variant = 'rocket',
  message,
  className = '',
}) => {
  return (
    <div className={`relative ${className}`}>
      {children}
      
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 backdrop-blur-sm rounded-xl z-10">
          <div className="text-center">
            <LoadingSpinner variant={variant} size="lg" color="orange" />
            {message && (
              <p 
                className="mt-4 text-sm font-medium"
                style={{ color: 'var(--space-text-primary)' }}
              >
                {message}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoadingSpinner; 